/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * App Component - Main Application Entry Point
 *
 * Handles routing and global state initialization.
 * Supports both authenticated users and guest checkout.
 */

import * as React from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  Outlet,
} from "react-router-dom";
import { createLocalizeRouter } from "@/utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { Loading } from "@/components/loading";
import ResetPasswordPage from "@/pages/reset-password";
import { useGetProfileQuery } from "@/redux/services/profile";
import { buildDashboardCatalog } from "@/lib/dashboard-catalog";
import { useGetOrdersQuery } from "@/redux/services/orders";
import StripeAddress from "./components/cartpage/address";
import type { RootState } from "@/redux/store";
import { authClient } from "@/lib/auth-client";
import { getStoredAuthToken, getStoredRefreshToken } from "@/lib/token-cache";
import { logger } from "@/lib/logger";

const RootLayout = React.lazy(() => import("@/layouts/root"));
const DashboardLayout = React.lazy(() => import("@/layouts/dashboard"));
const DashboardHomePage = React.lazy(
  () => import("@/pages/dashboard/overview"),
);
const ProductsPage = React.lazy(() => import("@/pages/dashboard/products"));
const ProductDetailsPage = React.lazy(
  () => import("@/pages/dashboard/license-details"),
);

const AccountDetailsPage = React.lazy(
  () => import("@/pages/dashboard/account-settings"),
);
const BillingPaymentsPage = React.lazy(
  () => import("@/pages/dashboard/billing-payments"),
);
// S725 R-5: orders from the previous Deskshare store.
const OrderHistoryPage = React.lazy(
  () => import("@/pages/dashboard/order-history"),
);
const OrderHistoryDetailPage = React.lazy(
  () => import("@/pages/dashboard/order-history-detail"),
);

const AddToCartLookup = React.lazy(
  () => import("@/components/cartpage/add-to-cart-lookup"),
);
const CartPage = React.lazy(() => import("@/pages/cart"));
const ThankYouPage = React.lazy(() => import("@/pages/thank-you"));
const TranslationStartedPage = React.lazy(() => import("@/pages/translation-started"));
const CheckoutPage = React.lazy(() => import("@/pages/checkout"));
const ReturnPage = React.lazy(() =>
  import("@/pages/checkout").then((module) => ({ default: module.Return })),
);
const Listpage = React.lazy(() => import("@/pages/list"));
const PaymentErrorPage = React.lazy(
  () => import("@/pages/payment-error"),
);

const OfflineActivationPage = React.lazy(
  () => import("@/pages/offline-activation"),
);
const TrialVerifyPage = React.lazy(() => import("@/pages/trial-verify"));
const TrialRegisterPage = React.lazy(() => import("@/pages/trial-register"));
const TrialStatusPage = React.lazy(() => import("@/pages/trial-status"));
const TrialUnsubscribedPage = React.lazy(() => import("@/pages/trial-unsubscribed"));
const UnsubscribeCartPage = React.lazy(() => import("@/pages/unsubscribe-cart"));
const MigrationUnsubscribePage = React.lazy(() => import("@/pages/migration-unsubscribe"));
// The shop's /{lang}/refund-policy route. While REFUND_POLICY_URL and
// VITE_REFUND_POLICY_URL name this route (production, until the new website is
// live there) it shows the shop's stand-in policy. Once they name the website's
// page it forwards there, so e-mails already sent and Stripe pages that carry
// this address reach the one policy. The route stays after the switch; only the
// stand-in text goes (see src/pages/refund-policy.tsx).
const RefundPolicyPage = React.lazy(() => import("@/pages/refund-policy"));

// FCR S771 (#43): the language-neutral address Stripe's settings hold.
const RefundPolicyLanguageRedirect = React.lazy(() =>
  import("@/pages/refund-policy").then((module) => ({
    default: module.RefundPolicyLanguageRedirect,
  })),
);
// VM-2 (S787): the cart's own sign-out page, which the Deskshare website sends
// the browser through after its own sign-out (see src/pages/auth/logout.tsx).
const ShopLogoutPage = React.lazy(() => import("@/pages/auth/logout"));
const ErrorPage = React.lazy(() => import("@/pages/error"));
const RouteErrorBoundary = React.lazy(() =>
  import("@/pages/error").then((module) => ({
    default: module.RouteErrorBoundary,
  })),
);
const router = createBrowserRouter([
  ...createLocalizeRouter([
    {
      path: "/",
      element: (
        <React.Suspense fallback={<Loading />}>
          <RootLayout />
        </React.Suspense>
      ),
      errorElement: (
        <React.Suspense fallback={<Loading />}>
          <RouteErrorBoundary />
        </React.Suspense>
      ),
      children: [
        {
          path: "/",
          element: (
            // S531: This redirect is the unauthenticated root fallback — it fires
            // before any /:lang param is established. It correctly defaults to "en"
            // (the only fully-translated locale) so the router can then pick up the
            // user's real lang once they navigate further. All post-auth navigations
            // use useLangPrefix() and are fully language-aware.
            <Navigate to="/en/dashboard/overview" replace />
          ),
        },
        {
          path: "/dashboard",
          element: (
            <React.Suspense fallback={<Loading />}>
              <DashboardLayout />
            </React.Suspense>
          ),
          children: [
            // NF-3 (FCR Pass 8): resolve a bare /dashboard address to Overview.
            // Without an index route, React Router rendered the dashboard layout
            // with an empty outlet: the customer saw the sidebar, header and
            // breadcrumb with nothing in the content area. It does not look like
            // an error, so it does not prompt a support call -- it looks like an
            // account with nothing in it. Reached by customers who trim the
            // address bar or follow an older link.
            // S658: RELATIVE, not "/en/dashboard/overview". The root fallback
            // above is right to name "en" because it fires before any /:lang
            // exists; this one only ever runs when the address already carries
            // a language, so an absolute "/en/..." took a German customer who
            // trimmed the address bar out of German -- and the trimmed address
            // is the exact case this index route was added to serve. A relative
            // target resolves against the matched parent, /{lang}/dashboard, so
            // the language the customer arrived in is the language they keep.
            { index: true, element: <Navigate to="overview" replace /> },
            {
              path: "/dashboard/overview",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <DashboardHomePage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/products",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ProductsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/subscription/:id",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ProductDetailsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/product",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ProductDetailsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/billing-payments",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <BillingPaymentsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/order-history",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <OrderHistoryPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/order-history/:orderNumber",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <OrderHistoryDetailPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/account-settings",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <AccountDetailsPage />
                </React.Suspense>
              ),
            },
          ],
        },
        {
          path: "",
          element: (
            <React.Suspense fallback={<Loading />}>
              <Outlet />
            </React.Suspense>
          ),
          children: [
            {
              path: "/checkout",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <CheckoutPage />
                </React.Suspense>
              ),
            },
            {
              path: "/return",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ReturnPage />
                </React.Suspense>
              ),
            },
            {
              path: "/payment-error",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <PaymentErrorPage />
                </React.Suspense>
              ),
            },
            {
              // Issue #4 (from Deskshare Website Session 447): this is the
              // address every Buy Now link on the website points at, and it may
              // now name the product in words -- ?product=<lookup key> -- as
              // well as by Stripe identifiers. AddToCartLookup resolves the key
              // and rewrites the address before the cart mounts; with no key to
              // resolve it renders the cart immediately and does nothing else.
              path: "/add-to-cart",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <AddToCartLookup>
                    <CartPage />
                  </AddToCartLookup>
                </React.Suspense>
              ),
            },
            {
              path: "/cart",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <CartPage />
                </React.Suspense>
              ),
            },
            {
              // S-1 (FCR Pass 14): Clean basket URL — /en/Basket replaces
              // /en/add-to-cart?productId=...&priceId=... in the address bar
              // after the cart loads. Old links continue to work via /add-to-cart
              // and /cart. The CartPage reads its initial state from searchParams
              // (or from savePendingCartIntent sessionStorage) regardless of which
              // path it is mounted on.
              path: "/Basket",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <CartPage />
                </React.Suspense>
              ),
            },
            {
              path: "/list",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <Listpage />
                </React.Suspense>
              ),
            },
            {
              path: "/translation-started",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TranslationStartedPage />
                </React.Suspense>
              ),
            },
            {
              path: "/thanks",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ThankYouPage />
                </React.Suspense>
              ),
            },
            {
              path: "/offline/:data",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <OfflineActivationPage />
                </React.Suspense>
              ),
            },
            {
              path: "/address",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <StripeAddress />
                </React.Suspense>
              ),
            },
            {
              path: "/reset-password",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ResetPasswordPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/verify/:token",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialVerifyPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/register",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialRegisterPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/status",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialStatusPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/unsubscribed",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialUnsubscribedPage />
                </React.Suspense>
              ),
            },
            {
              path: "/unsubscribe-cart",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <UnsubscribeCartPage />
                </React.Suspense>
              ),
            },
            {
              path: "/refund-policy",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <RefundPolicyPage />
                </React.Suspense>
              ),
            },
            {
              // VM-2 (S787): ends the cart's session and returns to the
              // website. Two addresses, one page: /auth/logout is the address
              // the cart's review settled on, /signed-out the one the website's
              // review asked for -- the website's header Log Out works with
              // whichever it ships.
              path: "/auth/logout",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ShopLogoutPage />
                </React.Suspense>
              ),
            },
            {
              path: "/signed-out",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ShopLogoutPage />
                </React.Suspense>
              ),
            },
            {
              // S499: One-click opt-out for migration campaign emails.
              // Token is generated per OldCustomer row and embedded in the
              // offer email. Backend: GET /api/v1/migration-campaign/unsubscribe/
              path: "/migration/unsubscribe",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <MigrationUnsubscribePage />
                </React.Suspense>
              ),
            },
          ],
        },
        {
          path: "*",
          element: (
            <React.Suspense fallback={<Loading />}>
              <ErrorPage />
            </React.Suspense>
          ),
        },
      ],
    },
  ]),
  // FCR S771 (#43, Susmita T. Bug 6): the ONE address Stripe's Checkout settings
  // hold for the refund policy, in every language. It carries no language, so
  // it forwards to /{the shop's remembered language}/refund-policy, which shows
  // or forwards to the policy in that language. Outside the /:lang routes on
  // purpose: it is the address with no language in it.
  {
    path: "/refund-policy",
    element: (
      <React.Suspense fallback={<Loading />}>
        <RefundPolicyLanguageRedirect />
      </React.Suspense>
    ),
  },
]);

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state: RootState) => state.user.isAuthenticated,
  );

  const { data: session } = authClient.useSession();
  // TA-3 (S576): a stored token IS a session. Gating on the session object alone
  // leaves a customer who has just been handed a token looking like a guest for
  // one render, which is long enough for the skipped queries never to run -- the
  // dashboard then stays empty for a customer who has just paid.
  // Item 7 (FCR S687): a stored REFRESH token is a session too. Without this,
  // every query on this page is skipped for a signed-in customer whose access
  // token has expired -- and because it is a `skip`, nothing re-runs when the
  // renewal lands. The dashboard stays empty rather than briefly stale.
  const hasJwtSession = Boolean(getStoredAuthToken() || getStoredRefreshToken());
  const hasValidSession = Boolean(session?.user) || hasJwtSession;

  // Only fetch profile and orders for authenticated users
  // Skip for guests to allow guest checkout without API errors
  const {
    data: stripeCustomer,
    error: profileError,
    isLoading: profileLoading,
    isUninitialized: profileUninitialized,
  } = useGetProfileQuery(undefined, {
    skip: !hasValidSession && !isAuthenticated,
  });
  const {
    data: orders,
    error: ordersError,
    isLoading: ordersLoading,
    isUninitialized: ordersUninitialized,
  } = useGetOrdersQuery(undefined, {
    skip: !hasValidSession && !isAuthenticated,
  });

  React.useEffect(() => {
    // Log API errors for debugging (only when authenticated)
    if (profileError && (hasValidSession || isAuthenticated)) {
      logger.error("Profile API error:", profileError);
    }
    if (ordersError && (hasValidSession || isAuthenticated)) {
      logger.error("Orders API error:", ordersError);
    }
  }, [profileError, ordersError, hasValidSession, isAuthenticated]);

  // The catalogue build lives in its own module so it can be tested without
  // standing up the router. See src/lib/dashboard-catalog.ts for why the
  // readiness flag is never cleared here.
  React.useEffect(() => {
    buildDashboardCatalog({
      stripeCustomer,
      orders,
      isLoading: profileLoading || ordersLoading,
      // VS-2 (S712): a SKIPPED query reports isLoading: false with no data, and
      // the catalogue builder cannot tell that from a request that finished and
      // found nothing. Before sign-in both are skipped, so the catalogue was
      // marked ready and empty and License Details believed it.
      isUninitialized: profileUninitialized || ordersUninitialized,
      hasSession: hasValidSession || isAuthenticated,
      dispatch,
    });
  }, [
    stripeCustomer,
    orders,
    dispatch,
    profileLoading,
    ordersLoading,
    profileUninitialized,
    ordersUninitialized,
    hasValidSession,
    isAuthenticated,
  ]);

  return (
    <React.Suspense fallback={<Loading />}>
      <RouterProvider router={router} />
    </React.Suspense>
  );
}

export default App;
